import { convertDate2 } from 'utils';
import { ACTIVITY_LOGS_PER_PAGE } from './activityLogsTableAmount';
import {
  SHOW_ALL_ACTIVITY_LOGS,
  ALL_ACTIVITY_SCOPE_ID,
  ALL_ACTIVITY_SCOPE_NAME,
  checkedIncludesShowAll,
  flattenCheckedToSelectionIds,
  selectionIdFromCheckedToken,
  scopeNodeSelectionIds,
  skuIdsCheckState,
  type ActivityGroupsModel,
  type ActivitySkuHierarchyGroup,
} from './activityGroupsModel';
import { hasActiveLogSearch } from './logSearch';
import type {
  LogSearchFilter,
  LogSearchOptions,
  LogsValueMode,
} from './redux';

export type ActivityLogsSearchMode = 'latest' | 'search';

export type ActivityLogsSearchPayload = {
  mode: ActivityLogsSearchMode
  page: number
  pageSize: number
  useTimeRange: boolean
  startDate?: string | null
  endDate?: string | null
  logSearches: LogSearchFilter[]
  logSearch: string
  logSearchOptions: LogSearchOptions
  checked: string[]
  /** API dual-pane: scopes from left pane (Groups). */
  scopesChecked: string[]
  /** API dual-pane: always Show all after SKUs tab removal. */
  skuChecked: string[]
  valueMode: LogsValueMode
  allItems: boolean
  cashIn: boolean
  cashOut: boolean
  onlyLogIds?: string[] | null
  startLogId?: string | null
  endLogId?: string | null
  viewAs?: {
    permission: number
    staffId: string | null
    username: string | null
  } | null
  associateElevatedActivityLogs: boolean
  isComplianceActivity: boolean
  complianceActivityStaffId?: string | null
  /** include/only so AI and workflow can read undated task logs. Default exclude. */
  taskLogMode?: 'exclude' | 'include' | 'only'
};

export type ActivityLogsSearchCriteriaSource = {
  logSearches: LogSearchFilter[]
  logSearch: string
  logSearchOptions: LogSearchOptions
  checked: string[]
  valueMode: LogsValueMode
  allItems: boolean
  cashIn: boolean
  cashOut: boolean
  useTimeRange: boolean
  startDate?: string | Date | null
  endDate?: string | Date | null
  onlyLogIds?: string[] | null
  startLogId?: string | null
  endLogId?: string | null
  viewAs?: ActivityLogsSearchPayload['viewAs']
  associateElevatedActivityLogs: boolean
  isComplianceActivity: boolean
  complianceActivityStaffId?: string | null
  /** Last activity-scope / SKU the user turned on in the left pane. */
  lastSelectedScopeId?: string | null
};

const toIso = (value?: string | Date | null): string | null => {
  if (value == null || value === '') return null
  const d = new Date(value)
  return Number.isFinite(+d) ? d.toISOString() : null
}

/** Body for POST /searchlogs from the current /logs filters. */
export const buildActivityLogsSearchPayload = (
  source: ActivityLogsSearchCriteriaSource,
  mode: ActivityLogsSearchMode = 'search',
  page = 1,
): ActivityLogsSearchPayload => {
  const useTimeRange = Boolean(source.useTimeRange && source.startDate && source.endDate)
  const safePage = Number.isFinite(page) && page >= 1 ? Math.floor(page) : 1
  return {
    mode,
    page: safePage,
    pageSize: ACTIVITY_LOGS_PER_PAGE,
    useTimeRange,
    startDate: useTimeRange ? toIso(source.startDate) : null,
    endDate: useTimeRange ? toIso(source.endDate) : null,
    logSearches: mode === 'latest' ? [] : (source.logSearches || []),
    logSearch: mode === 'latest' ? '' : (source.logSearch || ''),
    logSearchOptions: source.logSearchOptions,
    // Keep parent>child occurrence keys so search can scope by branch department.
    checked: mode === 'latest'
      ? [SHOW_ALL_ACTIVITY_LOGS]
      : (source.checked || []).filter((c) => c && c !== SHOW_ALL_ACTIVITY_LOGS),
    scopesChecked: mode === 'latest'
      ? [SHOW_ALL_ACTIVITY_LOGS]
      : (source.checked || []),
    skuChecked: [SHOW_ALL_ACTIVITY_LOGS],
    valueMode: source.valueMode === 'units' ? 'units' : 'value',
    allItems: mode === 'latest' ? true : source.allItems !== false,
    cashIn: source.cashIn !== false,
    cashOut: source.cashOut !== false,
    onlyLogIds: mode === 'latest' ? null : (source.onlyLogIds?.length ? source.onlyLogIds : null),
    startLogId: mode === 'latest' ? null : (source.startLogId || null),
    endLogId: mode === 'latest' ? null : (source.endLogId || null),
    viewAs: source.viewAs || null,
    associateElevatedActivityLogs: Boolean(source.associateElevatedActivityLogs),
    isComplianceActivity: mode === 'latest' ? false : Boolean(source.isComplianceActivity),
    complianceActivityStaffId: source.complianceActivityStaffId || null,
  }
}

const SCOPE_SUMMARY_MAX_LINES = 4;

const nameForScopeId = (
  id: string,
  model?: ActivityGroupsModel | null,
): string => {
  const key = String(id || '').trim();
  if (!key || key === SHOW_ALL_ACTIVITY_LOGS) return '';
  const group = model?.byId.get(key);
  if (group?.name) return String(group.name).trim();
  const groupType = model?.groupTypeById.get(key);
  if (groupType?.name) return String(groupType.name).trim();
  const sku = (model?.skus || []).find((s) => s.id === key);
  if (sku?.name) return String(sku.name).trim();
  let found = '';
  const walk = (nodes: ActivitySkuHierarchyGroup[]) => {
    (nodes || []).forEach((node) => {
      if (found) return;
      if (node.id === key) found = String(node.name || '').trim();
      else if (node.children?.length) walk(node.children);
    });
  };
  walk(model?.skuHierarchy || []);
  return found;
};

const scopeNodeIsInSearch = (
  node: ActivitySkuHierarchyGroup,
  checked: string[],
  showAll: boolean,
): boolean => {
  if (showAll) return true;
  if (skuIdsCheckState(scopeNodeSelectionIds(node), checked) !== 'off') return true;
  return skuIdsCheckState(node.skuIds || [], checked) !== 'off';
};

const pushUniqueName = (names: string[], seen: Set<string>, raw: string) => {
  const label = String(raw || '').trim();
  if (!label) return;
  const key = label.toLowerCase();
  if (seen.has(key)) return;
  seen.add(key);
  names.push(label);
};

/**
 * Selected activity-scope and SKU names.
 * Last pick leads; remaining names stay in left-pane order.
 */
const checkedScopeNamesLastSelectedFirst = (
  checked: string[],
  model?: ActivityGroupsModel | null,
  lastSelectedScopeId?: string | null,
): string[] => {
  if (!checked.length) return [];
  const showAll = checkedIncludesShowAll(checked);
  const selectedIds = new Set(flattenCheckedToSelectionIds(checked, model));
  const names: string[] = [];
  const seen = new Set<string>();
  const walk = (nodes: ActivitySkuHierarchyGroup[]) => {
    (nodes || []).forEach((node) => {
      if (node.isObjectGroup) {
        (node.skus || []).forEach((sku) => {
          if (showAll || selectedIds.has(sku.id)) pushUniqueName(names, seen, sku.name);
        });
        return;
      }
      if (scopeNodeIsInSearch(node, Array.from(selectedIds), showAll)) {
        pushUniqueName(names, seen, node.name);
      }
      (node.skus || []).forEach((sku) => {
        if (showAll || selectedIds.has(sku.id)) pushUniqueName(names, seen, sku.name);
      });
      if (node.children?.length) walk(node.children);
    });
  };
  walk(model?.skuHierarchy || []);

  // Last left-pane pick leads; Show all has no pick so the list stays in pane order.
  const lastName = nameForScopeId(
    selectionIdFromCheckedToken(lastSelectedScopeId || ''),
    model,
  );
  if (!lastName) return names;
  const lastKey = lastName.toLowerCase();
  return [lastName, ...names.filter((n) => n.toLowerCase() !== lastKey)];
};

const checkedSummary = (
  checked: string[],
  model?: ActivityGroupsModel | null,
  lastSelectedScopeId?: string | null,
): string => {
  if (!checked.length) return 'No activity scopes selected';
  const names = checkedScopeNamesLastSelectedFirst(checked, model, lastSelectedScopeId);
  if (names.length) return names.join(', ');
  if (checkedIncludesShowAll(checked) || (checked || []).includes(ALL_ACTIVITY_SCOPE_ID)) {
    const orgScope = model?.groups?.find((g) => g.kind === 'organization');
    return orgScope?.name || ALL_ACTIVITY_SCOPE_NAME;
  }
  return 'No activity scopes selected';
};

const searchTermsSummary = (
  logSearches: LogSearchFilter[],
  logSearch: string,
): string => {
  const terms = [
    ...logSearches.map((f) => {
      const t = String(f.term || '').trim()
      if (!t) return ''
      return f.options?.negate ? `not “${t}”` : `“${t}”`
    }),
    logSearch.trim() ? `“${logSearch.trim()}”` : '',
  ].filter(Boolean)
  if (!terms.length) return 'No search terms'
  return terms.join(', ')
}

/** Same titles as the top-left activityLogs search-options dropdown. */
const SEARCH_DROPDOWN_OPTION_LABELS: {
  key: keyof Pick<LogSearchOptions, 'exactMatch' | 'negate' | 'hideArchives' | 'fromFullLogs'>
  label: string
}[] = [
  { key: 'exactMatch', label: 'Exact match' },
  { key: 'negate', label: 'Exclude matches' },
  { key: 'fromFullLogs', label: 'Add from prior results' },
  { key: 'hideArchives', label: 'Hide archives' },
]

const SEARCH_DROPDOWN_MATCH_LABELS: Record<string, string> = {
  room: 'Room name',
  staff: 'Staff username',
  customer: 'Guest',
  department: 'Department',
  ota: 'OTA',
}

/** Plain search terms (chips + draft) — shown as text, not badges. */
export const buildActivityLogsSearchTermLabels = (
  logSearches: LogSearchFilter[],
  logSearch: string,
): string[] => {
  const terms: string[] = [];
  const seen = new Set<string>();
  const push = (raw: string) => {
    const label = String(raw || '').trim();
    if (!label) return;
    const key = label.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    terms.push(label);
  };
  (logSearches || []).forEach((f) => push(f.term));
  push(logSearch);
  return terms;
};

/** Dropdown option labels only — Exact match, Hide archives, etc. */
export const buildActivityLogsSearchOptionBadges = (
  logSearches: LogSearchFilter[],
  logSearchOptions: LogSearchOptions,
): string[] => {
  const badges: string[] = [];
  const seen = new Set<string>();
  const push = (label: string) => {
    const key = label.toLowerCase();
    if (!label || seen.has(key)) return;
    seen.add(key);
    badges.push(label);
  };
  const pushOptions = (options?: LogSearchOptions | null) => {
    if (!options) return;
    SEARCH_DROPDOWN_OPTION_LABELS.forEach(({ key, label }) => {
      if (options[key]) push(label);
    });
    const matchLabel = SEARCH_DROPDOWN_MATCH_LABELS[options.matchMode];
    if (matchLabel) push(matchLabel);
  };

  (logSearches || []).forEach((f) => pushOptions(f.options));
  pushOptions(logSearchOptions);
  return badges;
};

/** @deprecated Use buildActivityLogsSearchTermLabels + buildActivityLogsSearchOptionBadges. */
export const buildActivityLogsSearchDropdownBadges = (
  logSearches: LogSearchFilter[],
  logSearch: string,
  logSearchOptions: LogSearchOptions,
): string[] => [
  ...buildActivityLogsSearchTermLabels(logSearches, logSearch),
  ...buildActivityLogsSearchOptionBadges(logSearches, logSearchOptions),
];

const valueSummary = (
  valueMode: LogsValueMode,
  allItems: boolean,
  cashIn: boolean,
  cashOut: boolean,
): string => {
  if (valueMode === 'units') return allItems ? 'All items · units' : 'Unit logs only'
  if (allItems) return 'All items'
  if (cashIn && cashOut) return 'Cash in and cash out'
  if (cashIn) return 'Cash in only'
  if (cashOut) return 'Cash out only'
  return 'Cash filters off'
}

export type ActivityLogsSearchSummaryIcon =
  | 'scopes'
  | 'time'
  | 'value'
  | 'units'
  | 'search'
  | 'report';

export type ActivityLogsSearchSummaryLine = {
  label: string
  value: string
  icon: ActivityLogsSearchSummaryIcon
  /** Clamp the value to this many lines (Scopes). */
  maxLines?: number
  /** Search row — plain text terms on their own line(s). */
  searchTerms?: string[]
  /** Search row — dropdown options as light blue badges below terms. */
  searchOptionBadges?: string[]
}

/** Lines shown on the /logs search prompt. */
export const buildActivityLogsSearchSummary = (
  source: ActivityLogsSearchCriteriaSource,
  model?: ActivityGroupsModel | null,
): ActivityLogsSearchSummaryLine[] => {
  const lines: ActivityLogsSearchSummaryLine[] = [
    {
      label: 'Scopes',
      value: checkedSummary(source.checked || [], model, source.lastSelectedScopeId),
      icon: 'scopes',
      maxLines: SCOPE_SUMMARY_MAX_LINES,
    },
  ]
  if (source.useTimeRange && source.startDate && source.endDate) {
    lines.push({
      label: 'Time',
      value: `${convertDate2(new Date(source.startDate))} – ${convertDate2(new Date(source.endDate))}`,
      icon: 'time',
    })
  } else {
    lines.push({ label: 'Time', value: 'All time', icon: 'time' })
  }
  const searchTerms = buildActivityLogsSearchTermLabels(
    source.logSearches || [],
    source.logSearch || '',
  );
  const searchOptionBadges = buildActivityLogsSearchOptionBadges(
    source.logSearches || [],
    source.logSearchOptions,
  );
  if (
    hasActiveLogSearch(source.logSearches || [], source.logSearch || '')
    || searchTerms.length
    || searchOptionBadges.length
  ) {
    lines.push({
      label: 'Search',
      value: searchTermsSummary(source.logSearches || [], source.logSearch || ''),
      icon: 'search',
      searchTerms: searchTerms.length ? searchTerms : undefined,
      searchOptionBadges: searchOptionBadges.length ? searchOptionBadges : undefined,
    });
  }
  lines.push({
    label: 'Value',
    value: valueSummary(source.valueMode, source.allItems, source.cashIn, source.cashOut),
    icon: source.valueMode === 'units' ? 'units' : 'value',
  })
  if (source.onlyLogIds?.length) {
    lines.push({
      label: 'Report logs',
      value: `${source.onlyLogIds.length} pinned log${source.onlyLogIds.length === 1 ? '' : 's'}`,
      icon: 'report',
    })
  }
  return lines
}

export const latestLogsButtonLabel = `Latest ${ACTIVITY_LOGS_PER_PAGE} logs`
